<template>
  <div 
    ref="containerRef"
    :class="['relative', props.widthClass]"
  >
    <button
      @click.stop="toggleDropdown"
      type="button"
      :class="['inline-flex items-center justify-between gap-2 border text-gray-500 rounded-md py-2.5 pl-3 pr-3 h-auto whitespace-nowrap focus:outline-none focus:ring-1 focus:ring-tuin-100 focus:border-tuin-100',
        props.fontSize, props.widthClass === 'w-fit' ? 'min-w-full w-max' : props.widthClass, props.error ? 'border-red-500 bg-red-50' : 'border-gray-300 bg-white'
      ]"
    >
      <slot name="trigger" :display-text="displayText" :selected="selectedOption">
        <span>{{ displayText }}</span>
      </slot>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="13"
        height="7"
        viewBox="0 0 13 7"
        fill="none"
        :class="['transition-transform duration-200 shrink-0', isOpen ? 'rotate-180' : '']"
      >
        <path fill-rule="evenodd" clip-rule="evenodd" d="M0.146894 0.146894C0.19334 0.10033 0.248515 0.0633873 0.30926 0.0381808C0.370005 0.0129744 0.435127 0 0.500894 0C0.566661 0 0.631782 0.0129744 0.692528 0.0381808C0.753273 0.0633873 0.808448 0.10033 0.854894 0.146894L6.50089 5.79389L12.1469 0.146894C12.1934 0.100406 12.2486 0.0635292 12.3093 0.0383701C12.37 0.013211 12.4352 0.000261784 12.5009 0.000261784C12.5666 0.000261784 12.6317 0.013211 12.6925 0.0383701C12.7532 0.0635292 12.8084 0.100406 12.8549 0.146894C12.9014 0.193381 12.9383 0.248571 12.9634 0.30931C12.9886 0.370049 13.0015 0.43515 13.0015 0.500894C13.0015 0.566637 12.9886 0.631738 12.9634 0.692477C12.9383 0.753217 12.9014 0.808406 12.8549 0.854894L6.85489 6.85489C6.80845 6.90146 6.75327 6.9384 6.69253 6.96361C6.63178 6.98881 6.56666 7.00179 6.50089 7.00179C6.43513 7.00179 6.37001 6.98881 6.30926 6.96361C6.24852 6.9384 6.19334 6.90146 6.14689 6.85489L0.146894 0.854894C0.100331 0.808448 0.0633878 0.753272 0.0381813 0.692527C0.0129749 0.631782 0 0.566661 0 0.500894C0 0.435126 0.0129749 0.370005 0.0381813 0.30926C0.0633878 0.248515 0.100331 0.193339 0.146894 0.146894Z" fill="#FF4265"/>
      </svg>
    </button>
    <div
      v-show="isOpen"
      ref="dropdownRef"
      :class="dropdownClasses"
      :style="dropdownStyle"
    >
      <ul class="p-2 text-sm">
        <li v-for="option in renderedOptions" :key="option.value">
          <slot
            name="option"
            :option="option"
            :is-selected="isSelected(option.value)"
            :toggle="() => onToggle(option.value)"
          >
            <span 
              @click="onToggle(option.value)" 
              class="block px-2 py-2 cursor-pointer hover:bg-tuin-100 hover:text-white rounded-md text-custom_gray font-medium"
            >
              {{ option.name }}
            </span>
          </slot>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, nextTick } from 'vue';

const props = defineProps({
  modelValue:         { default: () => [] },
  options:            { type: Array,   default: () => [] },
  placeholder:        { type: String,  default: 'Seleccionar' },
  multiple:           { type: Boolean, default: true },
  showAll:            { type: Boolean, default: false },
  align:              { type: String, default: 'left', validator: (value) => ['left', 'right'].includes(value)},
  dynamicPlaceholder: { type: Boolean, default: true },
  widthClass:         { type: String, default: 'w-fit' },
  fontSize:           { type: String, default: 'text-sm' },
  error:              { type: Boolean, default: false }
});

const emit = defineEmits(['update:modelValue', 'open']);

const isOpen          = ref(false);
const containerRef    = ref(null);
const dropdownRef     = ref(null);
const openUpward      = ref(false);
const maxHeight       = ref(300);

const dropdownClasses = computed(() => {
  const baseClasses = 'absolute z-50 bg-white rounded-lg shadow-lg min-w-full w-max overflow-y-auto'
  const positionClass = openUpward.value ? 'bottom-full mb-1' : 'top-full mt-1'
  const alignClass = props.align === 'right' ? 'right-0' : 'left-0'
  return `${baseClasses} ${positionClass} ${alignClass}`
})

const dropdownStyle = computed(() => ({
  maxHeight: `${maxHeight.value}px`
}))

const displayText = computed(() => {
  // Si dynamicPlaceholder es false, siempre mostrar el placeholder estático
  if (!props.dynamicPlaceholder) {
    return props.placeholder;
  }

  // Si es múltiple y hay selecciones, mostrar el placeholder
  if (props.multiple && Array.isArray(props.modelValue) && props.modelValue.length > 0) {
    return props.placeholder;
  }

  // Si es selección única y hay un valor seleccionado, mostrar el nombre de la opción
  if (!props.multiple && props.modelValue) {
    const selectedOption = props.options.find(opt => opt.value === props.modelValue);
    return selectedOption ? selectedOption.name : props.placeholder;
  }

  // Si no hay selección, mostrar el placeholder
  return props.placeholder;
})

const renderedOptions = computed(() => {
  const base = props.showAll ? [{ value: '', name: 'Ver Todo' }] : [];
  return [...base, ...props.options];
});

const selectedOption = computed(() => {
  if (props.multiple || !props.modelValue) return null;
  return props.options.find(opt => opt.value === props.modelValue) || null;
});

function isSelected(value) {
  return props.multiple
    ? props.modelValue.includes(value)
    : props.modelValue === value;
}

function toggleDropdown() {
  if (!isOpen.value) {
    emit('open');
    isOpen.value = true;
    nextTick(() => adjustDropdownPosition());
  } else {
    isOpen.value = false;
  }
}

function adjustDropdownPosition() {
  if (!containerRef.value || !dropdownRef.value) return;
  const rect = containerRef.value.getBoundingClientRect();
  const vh = window.innerHeight;
  const spaceBelow = vh - rect.bottom;
  const spaceAbove = rect.top;
  const gap = 8;

  if (spaceBelow < 200 && spaceAbove > spaceBelow) {
    openUpward.value = true;
    maxHeight.value = Math.min(300, Math.max(150, spaceAbove - gap));
  } else {
    openUpward.value = false;
    maxHeight.value = Math.min(300, Math.max(150, spaceBelow - gap));
  }
}

function onToggle(value) {
  if (props.multiple) {
    const current = [...props.modelValue];
    const idx = current.indexOf(value);
    if (idx === -1) {
      current.push(value);
    } else {
      current.splice(idx, 1);
    }
    emit('update:modelValue', current);
  } else {
    emit('update:modelValue', value);
    isOpen.value = false;
  }
}

function handleClickOutside(event) {
  if (containerRef.value && !containerRef.value.contains(event.target)) {
    isOpen.value = false;
  }
}

onMounted(() => document.addEventListener('click', handleClickOutside));
onUnmounted(() => document.removeEventListener('click', handleClickOutside));

defineExpose({ close: () => { isOpen.value = false; } });
</script>
